import { PROMO_CODE_DEEPLINK_PREFIX } from "@constants";
import { StorageKey, UserReferral } from "@ctypes";
import { getQueryParams } from "./helpers";

const TOTAL_REFERRAL_STEPS = 3;

export const getReferralStatus = (referral?: UserReferral | null) => {
  if (!referral) return { current: 0, total: TOTAL_REFERRAL_STEPS };

  let current = 0;

  if (referral.signedUpAt) {
    current = current + 1;
  }
  if (referral.activatedAt) {
    current = current + 1;
  }
  if (referral.contributedAt) {
    current = current + 1;
  }

  return { current, total: TOTAL_REFERRAL_STEPS };
};

/**
 * Reads a promo code off the landing URL and remembers it for the tab.
 *
 * Two shapes arrive. A plain `?promoCode=` param is what our own links have always used. A OneLink
 * desktop fallback instead carries the AppsFlyer `deep_link_value`, prefixed —
 * `deep_link_value=promoCode_STARTDATE50` — which is the same value the mobile app reads, so one
 * printed link works on both platforms.
 *
 * Called once at startup rather than from the screen that needs it: the code only ever appears on
 * the URL the user lands on, and the entry flow navigates away from it before reaching the email
 * step. Stored in local storage, alongside the intake it will end up in, because someone can land
 * on a gift card link, drop off before the email step and come back days later — a tab-scoped copy
 * would be gone by then, and the deferred deep link is never delivered twice.
 *
 * Cleared once the code has been redeemed and on logout, so it cannot resurface in a later signup
 * on the same browser.
 */
export const captureDeeplinkPromoCode = () => {
  const params = getQueryParams(window.location.search);

  const readParam = (value?: string | string[]) => (Array.isArray(value) ? value[0] : value);

  const fromQuery = readParam(params.promoCode)?.trim();
  const deepLinkValue = readParam(params.deep_link_value)?.trim();

  const fromDeepLinkValue = deepLinkValue
    ?.toLowerCase()
    .startsWith(PROMO_CODE_DEEPLINK_PREFIX.toLowerCase())
    ? deepLinkValue.slice(PROMO_CODE_DEEPLINK_PREFIX.length).trim().toUpperCase()
    : undefined;

  const promoCode = fromQuery || fromDeepLinkValue;

  if (!promoCode) return undefined;

  // Wrapped because storage throws rather than no-ops in a private window, and losing the code
  // there should degrade to "no promo code" rather than break startup.
  try {
    localStorage.setItem(StorageKey.DeeplinkPromoCode, promoCode);
  } catch {
    // Ignored: the hook falls back to reading the URL directly.
  }

  return promoCode;
};

/** The promo code captured at startup, if this browser has seen one and not yet used it. */
export const getStoredDeeplinkPromoCode = () => {
  try {
    return localStorage.getItem(StorageKey.DeeplinkPromoCode) || undefined;
  } catch {
    return undefined;
  }
};

/** Drops the remembered code, once it has been redeemed or the session ends. */
export const clearDeeplinkPromoCode = () => {
  try {
    localStorage.removeItem(StorageKey.DeeplinkPromoCode);
  } catch {
    // Ignored: nothing was stored if storage is unavailable.
  }
};
